import type { Event, Hub } from '@sentry/browser';

import { buildSentryEvent } from './event-builder';
import type {
	CreateSentryLoggerOptions,
	SentryLogger,
	SentryLogLevel,
	SentryLogProps,
} from './types';

interface SentryClient {
	hub: Pick<Hub, 'withScope' | 'captureException' | 'captureMessage'>;
}

const stripQueryString = (value: string): string => value.split(/[?#]/)[0];

/** Removes query strings that may contain UGC before Sentry transmits request metadata. */
const beforeSend = (event: Event): Event => {
	if (event.request?.url) {
		event.request.url = stripQueryString(event.request.url);
	}

	if (event.request?.headers?.Referer) {
		event.request.headers.Referer = stripQueryString(event.request.headers.Referer);
	}
	if (event.request?.headers?.referer) {
		event.request.headers.referer = stripQueryString(event.request.headers.referer);
	}

	return event;
};

/**
 * Returns a memoized loader for an isolated Sentry hub. Server and non-production calls no-op, and
 * the browser SDK is loaded only when the first production event is logged.
 */
const createSentryClientLoader = ({
	dsn,
	getEnvironment,
	release,
}: CreateSentryLoggerOptions): (() => Promise<SentryClient | void>) => {
	let clientPromise: Promise<SentryClient | void> | undefined;

	return async () => {
		if (typeof window === 'undefined') {
			return;
		}

		const environment = getEnvironment();
		if (environment !== 'production') {
			return;
		}

		if (!clientPromise) {
			clientPromise = import(
				/* webpackChunkName: "@atlaskit-internal_@atlassian/smart-knowledge-cards/sentry-logger" */ '@sentry/browser'
			)
				.then((Sentry) => {
					const client = new Sentry.BrowserClient({
						dsn,
						attachStacktrace: true,
						release,
						environment,
						beforeSend,
						transport: Sentry.makeFetchTransport,
						stackParser: Sentry.defaultStackParser,
						// Keep event enrichment without instrumenting the host page.
						integrations: Sentry.getDefaultIntegrations({}).filter(
							({ name }) =>
								![
									'GlobalHandlers',
									'TryCatch',
									'BrowserApiErrors',
									'Breadcrumbs',
									'FunctionToString',
								].includes(name),
						),
					});

					return {
						hub: new Sentry.Hub(client),
					};
				})
				.catch(() => {
					// Sentry is best-effort. Chunk or client failures must not affect the product.
				});
		}

		return clientPromise;
	};
};

/**
 * Creates a best-effort logger that applies the shared taxonomy and captures through an isolated
 * Sentry client. Logging failures are intentionally swallowed so observability cannot affect the
 * consuming product.
 */
export const createSentryLogger = (options: CreateSentryLoggerOptions): SentryLogger => {
	const getSentryClient = createSentryClientLoader(options);
	let context: Pick<SentryLogProps, 'userId' | 'product'> = {};

	const log =
		(level: SentryLogLevel) =>
		async (props: SentryLogProps): Promise<void> => {
			try {
				const event = { ...context, ...props };
				const builtEvent = buildSentryEvent({
					...event,
					level,
					packageName: options.packageName,
				});
				const sentryClient = await getSentryClient();

				if (!sentryClient) {
					return;
				}

				sentryClient.hub.withScope((scope) => {
					Object.entries(builtEvent.tags).forEach(([key, value]) => {
						scope.setTag(key, value);
					});
					scope.setFingerprint(builtEvent.fingerprint);
					scope.setLevel(level);

					if (event.userId) {
						scope.setUser({ id: event.userId });
					}
					if (props.context) {
						scope.setContext('details', props.context);
					}

					if (builtEvent.capture.kind === 'exception') {
						sentryClient.hub.captureException(builtEvent.capture.error);
					} else {
						sentryClient.hub.captureMessage(builtEvent.capture.message);
					}
				});
			} catch {
				// Observability is best-effort; failures must not affect product behavior.
			}
		};

	return {
		setContext: (value) => {
			context = { ...value };
		},
		error: log('error'),
		warning: log('warning'),
		info: log('info'),
	};
};
